import { History, Users, CheckCircle, AlertCircle, Package } from 'lucide-react';
import type { AuditStats } from '../types/audit-logs.types';

interface AuditKpisProps {
  stats: AuditStats;
  isLoading?: boolean;
}

const skeleton = (
  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="bg-white p-4 rounded-md border border-slate-100 animate-pulse space-y-3">
        <div className="w-10 h-10 rounded-md bg-slate-100" />
        <div className="space-y-1.5">
          <div className="h-2.5 bg-slate-100 rounded w-2/3" />
          <div className="h-5 bg-slate-100 rounded w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

export function AuditKpis({ stats, isLoading }: AuditKpisProps) {
  if (isLoading) return skeleton;

  const total = stats.total || 0;
  const successRate = total > 0 ? Math.round((stats.successCount / total) * 100) : 0;
  const failRate = total > 0 ? Math.round((stats.failedCount / total) * 100) : 0;

  const kpiData = [
    { label: 'Tổng sự kiện', value: total.toLocaleString(), unit: 'sự kiện', icon: History, color: 'text-blue-600', bg: 'bg-blue-50', subValue: null },
    { label: 'Người dùng', value: (stats.uniqueUsers || 0).toLocaleString(), unit: 'người', icon: Users, color: 'text-emerald-600', bg: 'bg-emerald-50', subValue: null },
    { label: 'Thành công', value: (stats.successCount || 0).toLocaleString(), unit: 'lượt', icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', subValue: `${successRate}%` },
    { label: 'Thất bại', value: (stats.failedCount || 0).toLocaleString(), unit: 'lượt', icon: AlertCircle, color: 'text-rose-600', bg: 'bg-rose-50', subValue: `${failRate}%` },
    { label: 'Đối tượng ảnh hưởng', value: (stats.uniqueEntities || 0).toLocaleString(), unit: 'đối tượng', icon: Package, color: 'text-indigo-600', bg: 'bg-indigo-50', subValue: null },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {kpiData.map((kpi, index) => (
        <div key={index} className="bg-white p-4 rounded-md border border-slate-100 flex flex-col justify-between group hover:border-emerald-200 transition-all">
          <div className="flex items-center justify-between mb-2">
            <div className={`w-10 h-10 rounded-md ${kpi.bg} flex items-center justify-center`}>
              <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
            </div>
          </div>
          <div>
            <div className="sm:text-sm text-xs text-slate-500 mb-1">{kpi.label}</div>
            <div className="flex items-baseline gap-2">
              <span className="text-xl text-slate-800 tracking-tight">{kpi.value}</span>
              <span className="sm:text-sm text-xs text-slate-400">{kpi.unit}</span>
            </div>
            {kpi.subValue && (
              <div className="mt-2 flex items-center justify-between">
                <div className="h-1 flex-1 bg-slate-100 rounded-full overflow-hidden mr-3">
                  <div
                    className={`h-full rounded-full ${kpi.label === 'Thành công' ? 'bg-emerald-500' : 'bg-rose-500'}`}
                    style={{ width: kpi.subValue }}
                  />
                </div>
                <span className="text-xs text-slate-500">{kpi.subValue}</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
